import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type ListingProductListItem = {
  catalogProductId: string;
  productName: string;
  imageUrl: string | null;
  listingCount: number;
  shopCount: number;
  skuCount: number;
  activeCount: number;
  hiddenCount: number;
  removedCount: number;
  lastUpdatedAt: string;
  listingIds: string[];
};

export type ListingProductsListResult = {
  data: ListingProductListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type ListingProductShopRow = {
  listingId: string;
  shopId: string;
  shopName: string;
  shopSku: string;
  status: string;
  visible: boolean;
  price: number;
  currency: string;
  displayPrice: number | null;
  displayCurrency: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ListingProductSkuGroup = {
  catalogSkuId: string;
  oemPartNo: string | null;
  condition: string | null;
  fitmentStatus: string;
  imageUrl: string | null;
  shops: ListingProductShopRow[];
};

export type ListingProductDetail = {
  catalogProductId: string;
  productName: string;
  productDescription: string | null;
  categoryName: string | null;
  imageUrl: string | null;
  listingCount: number;
  shopCount: number;
  skuCount: number;
  skus: ListingProductSkuGroup[];
};

export type ListingListItem = {
  id: string;
  shopId: string;
  skuId: string;
  status: string;
  visible: boolean;
  createdAt: string;
  updatedAt: string;
  productName: string;
  sku: string;
  price: number;
  currency: string;
  shopName: string;
  imageUrl: string | null;
};

export type ListingsListResult = {
  data: ListingListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type ListListingsParams = OffsetPageQueryDTO & {
  q?: string;
  recentDays?: number;
};

export type BulkCreateModerationListingPayload = {
  catalogSkuId: string;
  shopIds: string[];
};

export type BulkCreateModerationListingResult = {
  results: Array<
    | {
        shopId: string;
        ok: true;
        listing: {
          id: string;
          shopId: string;
          skuId: string;
          status: string;
          visible: boolean;
        };
        created: boolean;
      }
    | { shopId: string; ok: false; error: string; reasons?: string[] }
  >;
  created: number;
  updated: number;
  failed: number;
};

export type UnlistedCatalogProductItem = {
  id: string;
  name: string;
  type: string;
  status: string;
  categoryName: string | null;
  brandName: string | null;
};

export type UnlistedCatalogSkuItem = {
  id: string;
  catalogProductId: string;
  oemPartNo: string | null;
  condition: string | null;
  fitmentStatus: "pending" | "complete";
};

export type ShopCatalogSkuListingItem = {
  shopId: string;
  shopName: string;
  shopStatus: string;
  city: string | null;
  shopProductSkuId: string | null;
  shopSku: string | null;
  listingId: string | null;
  listingStatus: string | null;
  listingVisible: boolean | null;
  hasListing: boolean;
  eligible: boolean;
  eligibilityReasons: string[];
  availableQty: number;
};

export type CreateModerationListingPayload = {
  shopId: string;
  shopProductSkuId: string;
  fulfillmentBranchId?: string | null;
};

export type CreateModerationListingResult =
  | {
      ok: true;
      listing: {
        id: string;
        shopId: string;
        skuId: string;
        status: string;
        visible: boolean;
      };
      created: boolean;
    }
  | { ok: false; error: string; reasons?: string[] };

export type ListingDetail = {
  id: string;
  shopId: string;
  skuId: string;
  status: string;
  visible: boolean;
  createdAt: string;
  updatedAt: string;
  productName: string;
  productDescription: string | null;
  sku: string;
  oemPartNo: string | null;
  price: number;
  currency: string;
  shopPrice: number;
  shopCurrency: string;
  displayPrice: number | null;
  displayCurrency: string | null;
  suggestedDisplayPrice: number | null;
  suggestedDisplayCurrency: string | null;
  shopName: string;
  categoryName: string | null;
  imageUrl: string | null;
};

export type BulkUpdateListingsPayload = {
  ids: string[];
  status: "active" | "hidden" | "removed";
  reason?: string;
};

export type BulkUpdateListingsResult = { updated: number };

export type UpdateListingPricePayload = {
  displayPrice: number | null;
  displayCurrency?: string | null;
};

export type ReportListItem = {
  id: string;
  reporterUserId: string;
  reporterName: string | null;
  targetType: string;
  targetId: string;
  reason: string | null;
  details: string | null;
  status: string;
  actionTaken: string | null;
  createdAt: string;
};

export type ReportsListResult = {
  data: ReportListItem[];
  meta: { total: number; limit: number; offset: number };
};

export type ListReportsParams = OffsetPageQueryDTO;

export type TargetPreview =
  | { type: "listing"; id: string; productName: string; shopName: string }
  | {
      type: "review";
      id: string;
      content: string | null;
      rating: number;
      listingId: string | null;
    }
  | { type: "shop"; id: string; name: string }
  | { type: "mechanic"; id: string; name?: string }
  | { type: "job"; id: string };

export type ReportAuditMetadata = {
  platform: string | null;
  ip: string | null;
  userAgent: string | null;
};

export type ReportDetail = {
  id: string;
  reporterUserId: string;
  reporterName: string | null;
  targetType: string;
  targetId: string;
  reason: string | null;
  details: string | null;
  status: string;
  actionTaken: string | null;
  createdAt: string;
  targetPreview: TargetPreview | null;
  reporterTotalReports: number;
  reporterActionRate: number;
  auditMetadata: ReportAuditMetadata;
};

export type UpdateReportStatusPayload =
  | { status: "reviewing" }
  | { action: "actioned" | "dismissed"; actionTaken: string };

export type ModerationHistoryItem =
  | {
      type: "listing";
      id: string;
      date: string;
      action: "hidden" | "removed" | "restored";
      targetType: "listing";
      targetId: string;
      targetDisplay: string;
      actorId: string | null;
      actorDisplay: string | null;
      details?: string;
    }
  | {
      type: "report";
      id: string;
      date: string;
      action: "actioned" | "dismissed";
      targetType: string;
      targetId: string;
      targetDisplay: string;
      actorId: string | null;
      actorDisplay: string | null;
      reporterName: string | null;
      reason: string | null;
      actionTaken: string | null;
    };

export type ListModerationHistoryParams = {
  limit?: number;
  offset?: number;
  fromDate?: string;
  toDate?: string;
  targetType?: string;
};

export type ListModerationHistoryResult = {
  data: ModerationHistoryItem[];
  meta: { total: number; limit: number; offset: number };
};

export const moderationApi = {
  async listListingProducts(params: ListListingsParams) {
    const url = new URL(`${config.apiUrl}/moderation/listings/products`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.q) url.searchParams.set("q", params.q);
    if (params.recentDays != null)
      url.searchParams.set("recentDays", String(params.recentDays));
    if (params.sort) url.searchParams.set("sort", JSON.stringify(params.sort));
    if (params.filters)
      url.searchParams.set("filters", JSON.stringify(params.filters));
    return fetcher.get<ListingProductsListResult>(url.toString());
  },

  async getListingProduct(catalogProductId: string) {
    return fetcher.get<ListingProductDetail>(
      `${config.apiUrl}/moderation/listings/products/${catalogProductId}`,
    );
  },

  async listListings(params: ListListingsParams) {
    const url = new URL(`${config.apiUrl}/moderation/listings`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.q) url.searchParams.set("q", params.q);
    if (params.recentDays != null)
      url.searchParams.set("recentDays", String(params.recentDays));
    if (params.sort) url.searchParams.set("sort", JSON.stringify(params.sort));
    if (params.filters)
      url.searchParams.set("filters", JSON.stringify(params.filters));
    return fetcher.get<ListingsListResult>(url.toString());
  },

  async createListing(payload: CreateModerationListingPayload) {
    return fetcher.post<CreateModerationListingResult>(
      `${config.apiUrl}/moderation/listings`,
      payload,
    );
  },

  async bulkCreateListings(payload: BulkCreateModerationListingPayload) {
    return fetcher.post<BulkCreateModerationListingResult>(
      `${config.apiUrl}/moderation/listings/bulk`,
      payload,
    );
  },

  async listUnlistedCatalogProducts(params: {
    limit?: number;
    offset?: number;
    search?: string;
  }) {
    const url = new URL(`${config.apiUrl}/moderation/listings/unlisted-products`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.search?.trim()) {
      url.searchParams.set("search", params.search.trim());
    }
    return fetcher.get<{
      data: UnlistedCatalogProductItem[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString());
  },

  async listUnlistedCatalogSkus(
    catalogProductId: string,
    params: { limit?: number; offset?: number; search?: string } = {},
  ) {
    const url = new URL(
      `${config.apiUrl}/moderation/listings/unlisted-products/${catalogProductId}/skus`,
    );
    url.searchParams.set("limit", String(params.limit ?? 50));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.search?.trim()) {
      url.searchParams.set("search", params.search.trim());
    }
    return fetcher.get<{
      data: UnlistedCatalogSkuItem[];
      meta: { total: number; limit: number; offset: number };
    }>(url.toString());
  },

  async listShopsForCatalogSku(catalogSkuId: string) {
    return fetcher.get<{ data: ShopCatalogSkuListingItem[] }>(
      `${config.apiUrl}/moderation/listings/catalog-skus/${catalogSkuId}/shops`,
    );
  },

  async getListing(id: string) {
    return fetcher.get<ListingDetail>(
      `${config.apiUrl}/moderation/listings/${id}`,
    );
  },

  async updateListingPrice(id: string, payload: UpdateListingPricePayload) {
    return fetcher.patch<ListingDetail>(
      `${config.apiUrl}/moderation/listings/${id}`,
      payload,
    );
  },

  async bulkUpdateListingsStatus(payload: BulkUpdateListingsPayload) {
    return fetcher.patch<BulkUpdateListingsResult>(
      `${config.apiUrl}/moderation/listings/bulk-status`,
      payload,
    );
  },

  async listReports(params: ListReportsParams) {
    const url = new URL(`${config.apiUrl}/moderation/reports`);
    url.searchParams.set("limit", String(params.limit ?? 20));
    url.searchParams.set("offset", String(params.offset ?? 0));
    if (params.sort) url.searchParams.set("sort", JSON.stringify(params.sort));
    if (params.filters)
      url.searchParams.set("filters", JSON.stringify(params.filters));
    return fetcher.get<ReportsListResult>(url.toString());
  },

  async getReport(id: string) {
    return fetcher.get<ReportDetail>(
      `${config.apiUrl}/moderation/reports/${id}`,
    );
  },

  async updateReportStatus(id: string, payload: UpdateReportStatusPayload) {
    return fetcher.patch<{ ok: boolean }>(
      `${config.apiUrl}/moderation/reports/${id}/status`,
      payload,
    );
  },

  async listModerationHistory(params: ListModerationHistoryParams) {
    const url = new URL(`${config.apiUrl}/moderation/history`);
    if (params.limit != null)
      url.searchParams.set("limit", String(params.limit));
    if (params.offset != null)
      url.searchParams.set("offset", String(params.offset));
    if (params.fromDate) url.searchParams.set("fromDate", params.fromDate);
    if (params.toDate) url.searchParams.set("toDate", params.toDate);
    if (params.targetType)
      url.searchParams.set("targetType", params.targetType);
    return fetcher.get<ListModerationHistoryResult>(url.toString());
  },
};
